import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook for chart-safe rendering in card components
 *
 * Charts need special handling when rendered inside collapsible cards or over AJAX:
 * 1. Wait for container dimensions to be available
 * 2. Wait for data to be loaded
 * 3. Trigger resize/reflow after card expansion
 * 4. Handle slow network conditions gracefully
 */
export function useChartSafeRender() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDimensionReady, setIsDimensionReady] = useState(false);
  const [resizeKey, setResizeKey] = useState(0);

  // Check if container has valid dimensions
  const checkDimensions = useCallback(() => {
    if (containerRef.current) {
      const { offsetWidth, offsetHeight } = containerRef.current;
      return offsetWidth > 0 && offsetHeight > 0;
    }
    return false;
  }, []);

  // Initialize dimension check
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (checkDimensions()) {
        setIsDimensionReady(true);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [checkDimensions]);

  // Trigger resize after card expansion
  const triggerResize = useCallback(() => {
    // This forces chart libraries to re-measure
    setResizeKey((prev) => prev + 1);

    // Also dispatch a resize event for libraries that listen to it
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('resize'));
    }
  }, []);

  return {
    containerRef,
    isDimensionReady,
    resizeKey,
    triggerResize,
    checkDimensions,
  };
}

/**
 * Hook for managing card expand/collapse with chart redraw
 *
 * When a card with a chart is collapsed/expanded, the chart needs to be redrawn
 * because its container dimensions changed.
 */
export function useChartExpandHandler(onExpandChange: () => void) {
  const handleExpandChange = useCallback(() => {
    // Wait for CSS animation to complete before redrawing chart
    // Most CSS transitions are 200-300ms
    setTimeout(onExpandChange, 250);
  }, [onExpandChange]);

  return handleExpandChange;
}

/**
 * Example usage in a chart card:
 *
 * function ChartCard({ card, userContext, onError }: CardComponentProps) {
 *   const [data, setData] = useState(null);
 *   const [collapsed, setCollapsed] = useState(false);
 *   const chart = useChartSafeRender();
 *
 *   useEffect(() => {
 *     // Load data from API
 *     loadChartData().then(setData).catch(onError);
 *   }, []);
 *
 *   const handleExpand = useChartExpandHandler(() => {
 *     // Trigger chart redraw when expanded
 *     chart.triggerResize();
 *   });
 *
 *   return (
 *     <div ref={chart.containerRef}>
 *       {chart.isDimensionReady && data && (
 *         <ChartLibrary key={chart.resizeKey} data={data} />
 *       )}
 *     </div>
 *   );
 * }
 */
